import { SELF_ALLOCATION_RETIRED } from "@/features/finances/selfAllocationFeature";
import { APPLICATION_APPROVAL_ENABLED } from "@/features/power_projects/applicationApprovalFeature";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import {
  FileStack,
  LayoutDashboard,
  ListChecks,
  ClipboardList,
  ShoppingCart,
  PhoneCall,
  FileCheck,
  Receipt,
  Building2,
  BookOpen,
  Sparkles,
  Radio,
  Calculator,
  FunctionSquare,
  Puzzle,
  HardHat,
  FileSignature,
  FileText,
  Wrench,
  UserCog,
  Users,
  CheckSquare,
  ScrollText,
  Wallet,
  Gavel,
  Target,
  AtSign,
  Landmark,
  Mail,
  Settings,
  CalendarDays,
  CalendarRange,
  TrendingUp,
  BarChart3,
  Shield,
  ShieldCheck,
  Clock3,
  Package,
  Boxes,
  UserSearch,
  Car as CarIcon,
  Truck,
  Bug,
  Activity,
  MessageSquare,
  FolderOpen,
  KeyRound,
  ListTodo,
  X,
  CalendarOff,
  Stethoscope,
  Monitor,
  Zap,
  MapPinned,
  Send,
  Inbox,
  UserPlus,
  Printer,
  PencilRuler,
  Upload,
  Dumbbell,
  Search,
  Layers,
  CalendarClock,
  Scale,
  FlaskConical,
  Split,
  Bot,
  Eye,
  BrainCircuit,
  Banknote,
  Video,
  FileSearch,
  LandPlot,
  Map,
  History,
  Loader2,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ChevronsDownUp,
  ArrowUpDown,
  BookUser,
  HeartHandshake,
  SlidersHorizontal,
  ClipboardCheck,
} from "lucide-react";
import { clsx } from "clsx";
import { SidebarSectionReorder } from "./SidebarSectionReorder";
import { DLC_BADGE_CHIP, DLC_BADGE_GLOSS } from "@/utils/dlc";
import { canManageContractors } from "@/features/contractors/utils";
import { PushNotificationBanner } from "@/features/chat/components/PushNotificationBanner";
import { useBillingPeriods } from "@/features/employee_agreements/hooks/useEmployeeAgreements";
// All sidebar badge counts now come from ONE cross-tab-deduplicated request.
// See hooks/useNavBadges (leader-election) + backend apps/core/nav_badges.py.
import { useEopStatus } from "@/features/eop_mail/api/mailbox";
import { useNavBadges, useNavBadgesLoading } from "@/hooks/useNavBadges";
import {
  OFFICIAL_FEE_BADGE_COLORS,
  OFFICIAL_FEE_BADGE_TITLES,
} from "@/features/official_fees/badges";
import {
  B2B_BADGE_COLORS,
  B2B_BADGE_TITLES,
  B2B_EMPLOYEE_BADGES,
} from "@/features/b2b_invoices/badges";
import { showsMyB2BInvoices, showsMyTimesheets } from "@/utils/contractViews";
import {
  TIMESHEET_MISSING_BADGE_COLOR,
  TIMESHEET_MISSING_BADGE_TITLE,
} from "@/features/timesheets/badges";
import { useTimesheetLock } from "@/features/timesheets/hooks/useTimesheets";
// Sekcja „Postęp prac" żyje w Friday — sidebar korzysta z tego samego źródła,
// żeby zsumować jej liczniki w jeden badge przy pozycji Friday.
import {
  useWorkProgressLinks,
  workProgressBadgeTotal,
  workProgressBadgeTone,
} from "@/features/tasks/hooks/useWorkProgressLinks";

/* Static colored badge on a nav item / section header. With `to` the badge itself is a
   link (e.g. "4 tyt." → portfolio filtered to the projects that wait), `title` its tooltip. */
interface NavBadge {
  text: string;
  cls: string;
  to?: string;
  title?: string;
}

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  /* Static colored badge (e.g. DLC marker) rendered on the right of the link. */
  badge?: NavBadge;
  /* Extra path prefixes that keep the link highlighted — detail pages that live
     under a legacy address while their list became a tab of a hub page. */
  alsoActive?: string[];
  /* Sub-paths that must NOT highlight this link — a sibling view nested under the
     same prefix that has its own menu entry (e.g. /contractors/suggestion-rules
     under /contractors). Without this both entries light up at once. */
  notActiveUnder?: string[];
}

/* Is `pathname` at or under one of the given paths? */
const underAny = (pathname: string, paths: string[] | undefined) =>
  (paths ?? []).some((p) => pathname === p || pathname.startsWith(`${p}/`));

interface NavGroup {
  label: string;
  items: NavItem[];
  /* Optional glossy marker rendered to the right of the section label. */
  badge?: NavBadge;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const item = (
  to: string,
  icon: React.ReactNode,
  label: string,
  end?: boolean,
  badge?: NavBadge,
  alsoActive?: string[],
): NavItem => ({ to, icon, label, end, badge, alsoActive });

/* Strips Polish diacritics so e.g. "urzedy" matches "Urzędy". NFD does not
   decompose ł/Ł, hence the extra replace. */
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");
const normalizeForSearch = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .replace(/ł/g, "l");

/* Reorders labeled sections according to the user's saved order. Sections that
   are not in the saved list (a module granted after the user last reordered)
   keep their default position: they are anchored just after the section that
   precedes them in the code order, so new modules never jump to the top. */
function applyNavGroupOrder(groups: NavGroup[], order: string[]): NavGroup[] {
  const headless = groups.filter((g) => !g.label);
  const labeled = groups.filter((g) => !!g.label);
  if (order.length === 0) return groups;
  // Plain records, not Map — `Map` is a lucide icon import in this file.
  const saved: Record<string, number> = {};
  order.forEach((label, i) => {
    saved[label] = i;
  });
  const rank: Record<string, number> = {};
  let last = -1;
  for (const g of labeled) {
    const at = saved[g.label];
    if (at !== undefined) {
      last = at;
      rank[g.label] = at;
    } else {
      rank[g.label] = last + 0.5;
    }
  }
  // Array.prototype.sort is stable, so ties (several unknown sections after the
  // same anchor) keep their default relative order.
  const sorted = [...labeled].sort(
    (a, b) => (rank[a.label] ?? 0) - (rank[b.label] ?? 0),
  );
  return [...headless, ...sorted];
}

/* Renders the small glossy DLC badge. Used on the section header and on items.
   A badge with `to` navigates on its own: the click cancels the surrounding NavLink's
   navigation (preventDefault — React Router honours it) and goes to `to` instead, while
   the NavLink's own onClick (closing the mobile menu) still runs. Nested <a> is invalid
   HTML, hence a span with role=link. */
function DlcBadge({ badge }: { badge: NavBadge }) {
  const navigate = useNavigate();
  if (!badge.to) {
    return (
      <span className={clsx(DLC_BADGE_CHIP, badge.cls)} title={badge.title}>
        <span className="relative z-10">{badge.text}</span>
      </span>
    );
  }
  const go = (e: React.SyntheticEvent) => {
    e.preventDefault();
    navigate(badge.to!);
  };
  return (
    <span
      role="link"
      tabIndex={0}
      title={badge.title}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") go(e);
      }}
      className={clsx(DLC_BADGE_CHIP, badge.cls, "cursor-pointer hover:ring-2 hover:ring-[var(--color-primary)]/40")}
    >
      <span className="relative z-10">{badge.text}</span>
    </span>
  );
}

type DotColor = "amber" | "red" | "brand" | "blue" | "rose" | "black";

/* A single dynamic badge on a nav item: a count, its color and an optional
   hover tooltip explaining what the count means. */
interface DotSpec {
  count: number;
  color: DotColor;
  title?: string;
}

/* Relative urgency of dot colors — lowest wins when aggregating a collapsed
   group into one badge (red/rose > brand > amber > blue). */
const DOT_PRIORITY: Record<DotColor, number> = {
  red: 0,
  rose: 1,
  black: 2,
  brand: 3,
  amber: 4,
  blue: 5,
};

/* Hover tooltips for the tender / ZE-tender action badges — same wording the
   user sees on the "Badge przetargów" settings screen (SettingsPage
   badgeOptions), so the sidebar dot and its meaning stay in sync. */
const TENDER_BADGE_TITLES = {
  needs_pricing: "Wymaga wyceny",
  needs_approval: "Oczekuje na zatwierdzenie wyceny",
  needs_offer: "Wymaga wystawienia oferty",
  needs_result: "Oferta złożona — minęło 21 dni bez wyniku",
} as const;

/* Collapses several dots into a single representative badge for a collapsed
   group header. Returns null when there is nothing to show. */
function aggregateDots(dots: DotSpec[]): DotSpec | null {
  const count = dots.reduce((acc, d) => acc + d.count, 0);
  if (count <= 0) return null;
  const color = dots.reduce<DotColor>(
    (best, d) => (DOT_PRIORITY[d.color] < DOT_PRIORITY[best] ? d.color : best),
    "blue",
  );
  // Combine the distinct per-dot tooltips so a collapsed group still explains
  // what its aggregate badge stands for (one meaning per line).
  const titles = Array.from(
    new Set(dots.map((d) => d.title).filter((t): t is string => !!t)),
  );
  return { count, color, title: titles.length ? titles.join("\n") : undefined };
}

/* Notification dot — always rounded-full (exception) */
function Dot({
  count,
  color = "amber",
  title,
}: {
  count: number;
  color?: DotColor;
  title?: string;
}) {
  if (count <= 0) return null;
  const cls =
    color === "red"
      ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
      : color === "brand"
        ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
        : color === "blue"
          ? "bg-blue-600 text-white"
          : color === "rose"
            ? "bg-rose-500 text-white"
            : color === "black"
              ? "bg-gray-900 text-white dark:bg-black"
              : "bg-amber-500 text-white";
  return (
    <span
      title={title}
      className={`text-[10px] font-mono rounded-full px-1.5 py-0.5 leading-none ${cls}`}
    >
      {count}
    </span>
  );
}

/* Missing-timesheet lock: the only links left in the nav are the ones the
   employee needs to file the overdue card — "Moje urlopy" / "Moje L4" / "Moje
   karty godzin" from Urlopy plus Harmonogram. Everything else (including the
   reviewer twins under Kadry) disappears until the card goes in; the matching
   route guard lives in ProtectedLayout. */
const TIMESHEET_LOCK_NAV_PATHS = new Set([
  "/timesheets",
  "/leaves",
  "/sick-leaves",
  "/schedule",
]);

function lockedToTimesheetGroups(groups: NavGroup[]): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((link) => TIMESHEET_LOCK_NAV_PATHS.has(link.to)),
    }))
    .filter((group) => group.items.length > 0);
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const isInternal = user?.role !== "contractor";
  const sidebarSearchEnabled = useUIStore((s) => s.sidebarSearchEnabled);
  const collapsedNavGroups = useUIStore((s) => s.collapsedNavGroups);
  const toggleNavGroup = useUIStore((s) => s.toggleNavGroup);
  const setNavGroupsCollapsed = useUIStore((s) => s.setNavGroupsCollapsed);
  const navGroupOrder = useUIStore((s) => s.navGroupOrder);
  // Missing last month's card → the nav is cut down to the views that let the
  // employee file it (see TIMESHEET_LOCK_NAV_PATHS below).
  const { isLocked: timesheetLocked } = useTimesheetLock();
  const setNavGroupOrder = useUIStore((s) => s.setNavGroupOrder);
  const resetNavGroupOrder = useUIStore((s) => s.resetNavGroupOrder);
  const [navQuery, setNavQuery] = useState("");
  /* Reorder mode swaps the nav links for a drag-and-drop list of section headers. */
  const [reorderMode, setReorderMode] = useState(false);

  const logoRef = useRef<HTMLImageElement>(null);
  const logoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [logoTooltipPos, setLogoTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const showLogoTooltip = () => {
    logoTimerRef.current = setTimeout(() => {
      if (logoRef.current) {
        const r = logoRef.current.getBoundingClientRect();
        setLogoTooltipPos({
          top: r.bottom + window.scrollY + 6,
          left: r.left + window.scrollX,
        });
      }
    }, 300);
  };
  const hideLogoTooltip = () => {
    if (logoTimerRef.current) clearTimeout(logoTimerRef.current);
    setLogoTooltipPos(null);
  };

  // Single cross-tab-deduplicated request for EVERY sidebar badge count.
  // The leader tab fetches /nav-badges/ and broadcasts; this tab may never touch
  // the network. The role flags below still gate which menu items render.
  const badges = useNavBadges();
  // Kopie robocze czekające na wysyłkę z eop@ — liczy je nav-badges (tylko dla
  // uprawnionych). Wcześniej menu co minutę w każdej karcie pobierało cały status
  // skrzynki (pięć COUNT-ów + statystyki tagowania) dla tej jednej liczby.
  // Status pobrany przez stronę skrzynki (enabled: false = tylko podsłuch cache, bez
  // własnych zapytań) ma pierwszeństwo: po wysłaniu kopii licznik zmienia się od razu,
  // a nie dopiero przy następnym odświeżeniu nav-badges.
  const eopWaiting =
    useEopStatus({ enabled: false }).data?.drafts_waiting ?? badges.eop_drafts_waiting ?? 0;
  // Spinner near the profile/settings row until the first badge payload lands.
  const navBadgesLoading = useNavBadgesLoading();

  const myActiveCount = badges.subtasks_my_active;

  // Only show "Moje rozliczenia podpisów" if there is at least one period with
  // a paid (non-free) item — otherwise the link would lead to an empty or
  // unbillable view (e.g. UoP signers with only free signatures).
  const { data: myBillingPeriods = [] } = useBillingPeriods();
  const hasPaidBillingPeriods = myBillingPeriods.some(
    (p) => Number(p.paid_items_count ?? 0) > 0,
  );
  const pendingApprovals = badges.tenders_pending_approvals;
  const tenderActionCounts = badges.tender_action_counts;
  const zeActionCounts = badges.ze_tender_action_counts;
  // Per-user badge preferences (which tender action-status counters to show in nav).
  // Missing (older persisted session) → treat as all-on.
  const tbp = user?.tender_badge_preference;
  const showBadge = {
    needs_pricing: tbp ? tbp.show_needs_pricing : true,
    needs_approval: tbp ? tbp.show_needs_approval : true,
    needs_offer: tbp ? tbp.show_needs_offer : true,
    needs_result: tbp ? tbp.show_needs_result : true,
  };
  const timesheetMissing = badges.timesheet_missing;
  const pendingOrdersCount = badges.orders_pending;
  const pendingProtocolsCount = badges.protocols_pending;
  const protocolsNeedingInvoiceCount = badges.protocols_needing_invoice;
  const protocolsNeedingHrMandateCount = badges.protocols_needing_hr_mandate;
  const isTechnicalDirector =
    user?.role === "director" &&
    (!!user?.is_technical_director || !!user?.is_ceo);
  const isGK = user?.role === "director" || user?.role === "coordinator";
  // DLC_02 correspondence badges: do odebrania (mine) + do zadekretowania (tech directors).
  const registerCounts = badges.correspondence;
  // DLC_01 aggregate: how many (non-muted) projects need this user's attention.
  const ppAttention = { projects: badges.power_projects_attention };
  const pendingRequisitionsCount = badges.requisitions_pending;
  const pendingChangeReqCount = badges.orders_change_requests_pending;
  const pendingExternalCostsCount = badges.orders_external_costs_pending;
  const staleUnconfirmedOrders = { count: badges.orders_stale_unconfirmed };
  const pendingCommentsCount = badges.orders_pending_comments;
  // Odebrane zamówienia bez oceny podwykonawcy, za które odpowiadam — czarny badge.
  const ordersAwaitingMyRating = badges.orders_awaiting_my_rating;
  // Podwykonawca wgrał pliki pozycji realizacyjnej — badge dla autora zapotrzebowania
  // (i koordynatorów): liczba nieprzeczytanych powiadomień o dostarczeniu plików.
  const resultFilesUploadedCount = badges.result_files_uploaded;
  const isDevOrDirector = !!(
    user?.is_developer ||
    (user?.role === "director" && isTechnicalDirector)
  );
  const whatsNewUnreadCount = badges.whats_new_unread;
  const openIssuesCount = badges.dev_issues_open;
  const lastTestRunBugCount = badges.last_test_run_bugs;
  const credentialAlertCount = badges.credentials_alert;
  const isAccountantOrDirector =
    user?.role === "accountant" || user?.role === "director";
  const pendingInvoicesCount = badges.invoices_pending;
  const invoicesNeedsActionCount = badges.invoices_needs_action;
  const expenseCounts = {
    pendingAcceptance: badges.expenses.pending_acceptance,
    pendingPayment: badges.expenses.pending_payment,
    pendingBooking: badges.expenses.pending_booking,
  };
  const isCeo = user?.role === "director" && !!user?.is_ceo;
  const przDraftCount = badges.prz_draft;
  const przInvestorCount = badges.prz_investor;
  const pendingKnowledgeCount = badges.knowledge_pending;
  // ARON write proposals waiting for my click — so an approval left hanging is
  // visible from anywhere in the app, not only inside the thread that raised it.
  const aronPendingWrites = badges.aron_pending_writes;
  const aronScheduledUnread = badges.aron_scheduled_unread;
  const engReviewsToReviewCount = badges.eng_reviews_to_review;
  const przNeedsApprovalCount = badges.prz_needs_approval;
  const przProformaToConvertCount = badges.prz_proforma_to_convert;
  const fleetTodo = badges.fleet_todo;
  const suppliesPendingCount = badges.supplies_pending;
  const assetsPendingCount = badges.assets_pending;
  const couriersPendingCount = badges.couriers_pending;
  const printOrdersPendingCount = badges.print_orders_pending;
  const isDirectorOrHR =
    user?.role === "director" ||
    user?.role === "hr" ||
    user?.role === "accountant";
  const canReviewSickLeaves = isDirectorOrHR || user?.role === "accountant";
  const pendingScopeRequestsCount = badges.rfq_scope_requests_pending;
  const openProceedingsCount = badges.rfq_open_proceedings;
  const proceedingsToResolveCount = badges.rfq_to_resolve;
  const accountRequestsCount = badges.rfq_account_requests;
  const pendingLeavesCount = badges.leaves_pending;
  const pendingSickLeavesCount = badges.sick_leaves_pending;
  const pendingRemoteWorkCount = badges.remote_work_pending;
  const pendingTimesheetsCount = badges.timesheets_pending;
  const todayInterviewsCount = badges.today_interviews;
  const isDirector = user?.role === "director";
  const expiringContractsCount = badges.expiring_contracts;
  const canSeeHealthBadge =
    isDirector || user?.role === "accountant" || user?.role === "hr";
  const healthTodoCount = badges.health_todo;
  const docPendingCount = badges.doc_pending;
  const docAwaitingCount = badges.doc_awaiting;
  const docUnacknowledgedCount = badges.doc_unacknowledged;
  const insuranceAlertCount = badges.insurance_alert;
  const weeklyReportPendingCount = badges.weekly_report_pending;
  const weeklyReportUnreadCount = badges.weekly_report_unread;
  const pendingFeeAcknowledgementCount =
    badges.official_fee_pending_acknowledgement;
  const canSeePayrollBadge = !!(
    user?.is_ceo ||
    user?.role === "accountant" ||
    user?.role === "hr"
  );
  const payrollMissingCount = badges.payroll_actuals_missing;
  const pendingFeeApprovalCount = badges.official_fee_pending_approval;
  const pendingFeePaymentCount = badges.official_fee_pending_payment;

  // Badge B2B w sidebarze — liczniki per powód, każdy wpis pokazuje tylko swoje:
  //   „Moje faktury B2B": zaległe miesiące / odrzucone / szkice (legenda na stronie)
  //   „Faktury B2B": SUBMITTED do zatwierdzenia (CEO), APPROVED/BOOKED/TRANSFERRED
  //   do rozliczenia (księgowy). `?.` — snapshot sprzed zmiany kształtu nie ma klucza.
  const b2bCounts = badges.b2b_invoices;

  // GG (chat) + TwoDrive now live in the TopBar, along with their live chat
  // badge/realtime wiring (useChatBadgeCounts / useChatGlobalLive).

  // Sekcja „Postęp prac" mieszka teraz w środku Friday (/tasks/board, /tasks/list).
  // W menu został sam Friday, więc jego badge musi objąć też liczniki tamtych
  // widoków — po wejściu w Friday sekcja pokazuje je znów rozdzielone.
  const workProgressLinks = useWorkProgressLinks();
  const workProgressCount = workProgressBadgeTotal(workProgressLinks);
  const workProgressTone = workProgressBadgeTone(workProgressLinks);

  const groups: NavGroup[] = [];

  groups.push({
    label: "",
    items: [item("/", <LayoutDashboard size={16} />, "Dashboard")],
  });

  const workItems: NavItem[] = [];
  // Zbiorcza tabela decyzji dyrektora — celowo BEZ plakietki (liczniki mają moduły).
  if (user?.role === "director" || user?.is_ceo) {
    workItems.push(
      item("/do-zatwierdzenia", <ListChecks size={16} />, "Do zatwierdzenia", true),
    );
  }
  if (
    user?.role !== "accountant" &&
    (user?.role !== "contractor" || user?.has_friday_access)
  ) {
    workItems.push(item("/tasks/board", <CheckSquare size={16} />, "Friday"));
  }
  // GG (chat) moved to the TopBar.
  // Spotkania zdalne — także podwykonawcy (widzą tylko te, na które są zaproszeni).
  workItems.push(item("/meetings", <Video size={16} />, "Spotkania"));
  // Harmonogram (urlopy/nieobecności zespołu) — narzędzie codziennej pracy, więc stoi
  // tutaj, a nie w „Urlopach"; jak wcześniej, tylko dla pracowników wewnętrznych.
  if (user?.role !== "contractor") {
    workItems.push(
      item("/schedule", <CalendarRange size={16} />, "Harmonogram", true),
    );
  }
  // „Postępy prac" przeniesione do sekcji „Postęp prac" wewnątrz Friday.
  if (
    (user?.role === "director" && isTechnicalDirector) ||
    user?.role === "coordinator"
  ) {
    workItems.push(
      item("/projects", <Building2 size={16} />, "Przedsięwzięcia"),
    );
    workItems.push(item("/clients", <Users size={16} />, "Klienci"));
  }
  workItems.push(
    item("/repository", <FolderOpen size={16} />, "Repozytorium", true),
  );
  // TwoDrive moved to the TopBar (weather-driven icon).
  workItems.push(
    item("/instructions", <BookOpen size={16} />, "Instrukcje", true),
  );
  // Co nowego — for everyone; subcontractors get only announcements targeting them
  // (scoped on the backend).
  workItems.push(
    item("/whats-new", <Sparkles size={16} />, "Co nowego", true),
  );
  // Wiadomości systemowe — wysyłka blokujących komunikatów do pracowników.
  // Tylko nadawcy: dyrektor / CEO / dev (lustro apps.system_messages.services.can_send).
  if (user?.role === "director" || user?.is_ceo || user?.is_developer) {
    workItems.push(
      item("/system-messages", <Radio size={16} />, "Wiadomości systemowe", true),
    );
  }
  // Rozkład pracy (własny formularz + wnioski) był w sekcji „Postęp prac" w
  // Friday (useWorkProgressLinks) — funkcja WYCOFANA, wpisy zniknęły; CEO ma
  // niżej archiwum dawnych wniosków (zob. selfAllocationFeature.ts).
  // Workload summary view — CEO (with money) or technical director (no money).
  const isWorkloadSummaryViewer =
    !!user &&
    (user.is_ceo || (user.role === "director" && !!user.is_technical_director));
  if (isWorkloadSummaryViewer) {
    workItems.push(
      item(
        "/finances/workload-summary",
        <Landmark size={16} />,
        "Rozkład pracy – podsumowanie",
        true,
      ),
    );
  }
  if (SELF_ALLOCATION_RETIRED && user?.is_ceo) {
    workItems.push(
      item(
        "/finances/self-allocations-admin",
        <History size={16} />,
        "Rozkład pracy – archiwum",
        true,
      ),
    );
  }
  // Contractors too, when granted — scoped on the backend to the picked branches.
  const canViewContacts =
    !!user &&
    (user.is_ceo ||
      user.role === "director" ||
      user.role === "coordinator" ||
      user.has_contacts_access);
  if (canViewContacts) {
    workItems.push(item("/contacts", <Users size={16} />, "Kontakty"));
  }
  if (workItems.length > 0) {
    groups.push({ label: "Praca", items: workItems });
  }

  {
    const tenderItems: NavItem[] = [];
    if (user?.has_view_tenders_permission) {
      tenderItems.push(
        item("/tenders", <Landmark size={16} />, "Przetargi", true),
      );
    }
    if (user?.has_view_pricing_catalog_permission) {
      tenderItems.push(
        item(
          "/tenders/pricing-catalog",
          <BookOpen size={16} />,
          "Katalog wycen",
        ),
      );
    }
    // Preset functions belong with the pricing catalog they are used from — same
    // audience as the API (catalog users + developers, who review without catalog access).
    if (user?.has_view_pricing_catalog_permission || user?.is_developer) {
      tenderItems.push(
        item(
          "/dev/preset-functions",
          <FunctionSquare size={16} />,
          "Funkcje wycen",
          true,
        ),
      );
    }
    if (tenderItems.length > 0) {
      groups.push({ label: "Przetargi", items: tenderItems });
    }
  }

  const dlcModules = user?.accessible_dlc_modules ?? [];

  // Estimating (przedmiar valuations) is gated behind the "Wyceny przedmiarowe"
  // permission — CEO and technical directors always, others only when granted.
  const canUseEstimating =
    !!user?.is_ceo ||
    (user?.role === "director" && !!user?.is_technical_director) ||
    !!user?.can_use_estimating;
  if (isInternal && canUseEstimating) {
    groups.push({
      label: "Kosztorysy",
      items: [
        item(
          "/estimating",
          <Calculator size={16} />,
          "Wycena kosztorysu",
          true,
        ),
        item(
          "/estimating/knowledge",
          <BrainCircuit size={16} />,
          "Baza wiedzy PRZ-ARON",
        ),
        item(
          "/estimating/custom-prices",
          <Banknote size={16} />,
          "Nasza baza cen",
        ),
        item(
          "/estimating/catalogs",
          <Layers size={16} />,
          "Katalogi i cenniki",
        ),
      ],
    });
  }

  if (user?.role !== "contractor") {
    const zakupyItems: NavItem[] = [];
    // Non-technical director enters the orders panel only with the CEO-granted
    // 'Zamawiający' permission (mirrors backend HasOrdersAccess + OrdersAccessGuard).
    const isOrdersDirector =
      user?.role === "director" &&
      (isTechnicalDirector || !!user?.has_orderer_permission);
    if (
      user?.role !== "accountant" &&
      (user?.role !== "director" || isOrdersDirector)
    ) {
      zakupyItems.push(
        item(
          "/requisitions",
          <ClipboardList size={16} />,
          "Zapotrzebowania usług zewnętrznych",
        ),
      );
    }
    if (
      user?.role === "coordinator" ||
      user?.role === "accountant" ||
      isOrdersDirector
    ) {
      zakupyItems.push(
        item(
          "/orders",
          <ShoppingCart size={16} />,
          "Zamówienia usług zewnętrznych",
        ),
      );
      zakupyItems.push(
        item(
          "/protocols",
          <FileCheck size={16} />,
          "Protokoły POCK/zaliczkowe",
        ),
      );
    } else if (user?.has_granted_order_access) {
      // Pracownik spoza ról z nadanym dostępem do konkretnego zamówienia (albo
      // zamawiający) — sama lista zamówień, bez protokołów (mirrors OrdersAccessGuard).
      zakupyItems.push(
        item(
          "/orders",
          <ShoppingCart size={16} />,
          "Zamówienia usług zewnętrznych",
        ),
      );
    }
    // Contractors / agreements also open up for holders of the CEO-granted
    // 'Podwykonawcy i umowy ramowe' permission (mirrors ContractorsAccessGuard).
    const isContractorsManager = canManageContractors(user);
    if (
      (user?.role === "director" && isTechnicalDirector) ||
      user?.role === "coordinator" ||
      isContractorsManager
    ) {
      zakupyItems.push({
        ...item("/contractors", <HardHat size={16} />, "Podwykonawcy"),
        // Zasady podpowiadania i megamapa mają własne wpisy — nie podświetlaj obu.
        notActiveUnder: ["/contractors/suggestion-rules", "/contractors/megamapa"],
      });
      zakupyItems.push(
        item(
          "/contractors/megamapa",
          <MapPinned size={16} />,
          "Megamapa podwykonawców",
        ),
      );
      zakupyItems.push(
        item("/agreements", <ScrollText size={16} />, "Umowy ramowe"),
      );
    } else if (user?.is_developer) {
      // Deweloper dostaje SAMĄ megamapę podwykonawców (ContractorAreaMapGuard),
      // bez listy firm i umów ramowych.
      zakupyItems.push(
        item(
          "/contractors/megamapa",
          <MapPinned size={16} />,
          "Megamapa podwykonawców",
        ),
      );
    }
    // Zasady podpowiadania podwykonawców — krąg „Podwykonawcy i umowy ramowe" (CEO,
    // dyrektor techniczny, flaga), jak backendowa bramka CanManageContractors na
    // /contractors/knowledge-notes/.
    if (isContractorsManager) {
      zakupyItems.push(
        item(
          "/contractors/suggestion-rules",
          <BrainCircuit size={16} />,
          "Zasady podpowiadania",
        ),
      );
    }
    if (
      (user?.role === "director" && isTechnicalDirector) ||
      user?.role === "coordinator"
    ) {
      zakupyItems.push(item("/penalties", <Gavel size={16} />, "Kary umowne"));
      zakupyItems.push(
        item(
          "/weekly-reports",
          <CalendarDays size={16} />,
          "Raporty tygodniowe",
        ),
      );
    }
    if (zakupyItems.length > 0) {
      groups.push({ label: "Zamówienia podwykonawcze", items: zakupyItems });
    }

    // Pozyskiwanie — firmy, których jeszcze NIE mamy. Osobna grupa od RFQ, bo to
    // etap wcześniej: tu dopiero szukamy firm, tam już zapraszamy do składania ofert.
    if (user?.is_ceo || user?.has_lead_permission) {
      groups.push({
        label: "Pozyskiwanie",
        items: [item("/leads", <Target size={16} />, "Leady")],
      });
    }

    // RFQ — postępowania ofertowe, w których to MY jesteśmy zamawiającym. Osobna
    // grupa, bo to odwrotny kierunek niż „Przetargi", w których startujemy sami.
    // Sekcję widzą tylko osoby z dostępem do RFQ (domyślnie dyr. tech / CEO / prowadzący,
    // pozostali po nadaniu) — zwykły pracownik nie ma tu czego szukać. Wyjątek: kto ma
    // „Podwykonawcy i umowy ramowe", redaguje słownik kategorii, więc dostaje samą tę pozycję.
    if (user?.has_rfq_view_permission) {
      groups.push({
        label: "RFQ",
        items: [
          item("/rfq/categories", <Layers size={16} />, "Kategorie i zakresy"),
          item("/rfq/proceedings", <Gavel size={16} />, "Postępowania"),
          item(
            "/rfq/scope-requests",
            <Inbox size={16} />,
            "Wnioski o rozszerzenie",
          ),
          item("/rfq/account-requests", <UserPlus size={16} />, "Prośby o konto"),
          item(
            "/rfq/declaration-questions",
            <ClipboardList size={16} />,
            "Domyślne oświadczenia",
          ),
          item(
            "/rfq/contract-templates",
            <FileSignature size={16} />,
            "Wzory umów (etapy ZE)",
          ),
        ],
      });
    } else if (isContractorsManager) {
      groups.push({
        label: "RFQ",
        items: [
          item("/rfq/categories", <Layers size={16} />, "Kategorie i zakresy"),
        ],
      });
    }
  }

  if (user?.role === "contractor") {
    const isDesignReviewer = !!user.contractor_company_is_design_reviewer;
    const contractorItems: NavItem[] = [
      item("/contractor/orders", <ShoppingCart size={16} />, "Zamówienia"),
      item("/contractor/map", <Map size={16} />, "Mapa zamówień"),
      // Prowadzenie projektów ZE (apps.contractor_led) — pozycja tylko, gdy firma jakieś ma.
      ...(badges.led_projects > 0
        ? [item("/contractor/projects", <ClipboardList size={16} />, "Prowadzone projekty")]
        : []),
      item("/contractor/todo", <ListTodo size={16} />, "Moje terminy"),
      // Urlopy firmy — tylko konto firmy, czyli konto z „Widocznością stawek" (ta sama
      // flaga, która daje kwoty i faktury); konta pracowników podwykonawcy tego nie widzą.
      ...(user.can_see_rates
        ? [item("/contractor/leaves", <CalendarOff size={16} />, "Moje urlopy")]
        : []),
      item("/contractor/protocols", <FileCheck size={16} />, "Protokoły POCK"),
      item("/contractor/invoices", <Receipt size={16} />, "Faktury"),
      item("/contractor/agreements", <ScrollText size={16} />, "Umowy ramowe"),
      item("/contractor/scope", <Layers size={16} />, "Mój zakres współpracy"),
      ...(user.can_access_rfq
        ? [
            item(
              "/contractor/proceedings",
              <Gavel size={16} />,
              "Moje postępowania ofertowe",
            ),
          ]
        : []),
      item(
        "/contractor/weekly-reports",
        <CalendarDays size={16} />,
        "Raporty tygodniowe",
      ),
    ];
    if (isDesignReviewer) {
      contractorItems.push(
        item(
          "/contractor/documentation",
          <FileText size={16} />,
          "Dokumentacja do sprawdzenia",
        ),
        item(
          "/contractor/doc-activity",
          <ClipboardList size={16} />,
          "Weryfikacja dokumentów",
        ),
      );
    }
    groups.push({ label: "Moje zlecenia", items: contractorItems });
  }

  const financeItems: NavItem[] = [];
  if (
    user?.role === "accountant" ||
    (user?.role === "director" && isTechnicalDirector)
  ) {
    financeItems.push(
      item("/accounting/invoices", <Calculator size={16} />, "Faktury P-GK"),
    );
  }
  // Accountant entry point for issuing UZ/UoD to natural-person contractors after POCK.
  if (user?.role === "accountant") {
    financeItems.push(
      item("/protocols", <FileCheck size={16} />, "Protokoły POCK"),
    );
  }
  if (
    (user?.role === "director" && isTechnicalDirector) ||
    user?.role === "accountant"
  ) {
    financeItems.push(
      item("/finances/invoices-prz", <FileText size={16} />, "Faktury PRZ"),
      item(
        "/finances/planned-revenues",
        <TrendingUp size={16} />,
        "Planowane przychody",
      ),
    );
    if (user?.has_consortium_access) {
      financeItems.push(
        item("/finances/consortium", <Users size={16} />, "Konsorcja"),
      );
    }
    const canSeeInternalCosts =
      user!.is_ceo ||
      user!.role === "accountant" ||
      (user!.role === "director" &&
        user!.internal_cost_access_level === "full");
    if (canSeeInternalCosts) {
      financeItems.push(
        item("/finances/costs", <Wrench size={16} />, "Koszty wewnętrzne"),
      );
    }
  }
  if (user?.role === "director" && !isTechnicalDirector) {
    // Non-technical director always sees branch-filtered internal costs and salary
    financeItems.push(
      item("/finances/costs", <Wrench size={16} />, "Koszty wewnętrzne"),
    );
    financeItems.push(
      item("/finances/salary", <Wallet size={16} />, "Koszty płac"),
    );
  } else if (user?.role === "director" || user?.role === "accountant") {
    financeItems.push(
      item("/finances/salary", <Wallet size={16} />, "Koszty płac"),
    );
  }
  if (user?.role !== "contractor") {
    financeItems.push(
      item("/expenses", <Receipt size={16} />, "Faktury kosztowe"),
    );
  }
  if (
    user?.role === "director" &&
    isTechnicalDirector &&
    user?.has_any_finance_access
  ) {
    financeItems.push(
      item(
        "/finances/order-costs",
        <BarChart3 size={16} />,
        "Macierz kosztów zleceń",
      ),
    );
  }
  if (financeItems.length > 0) {
    groups.push({ label: "Finanse", items: financeItems });
  }

  const isFinancePanel =
    user?.is_ceo ||
    user?.role === "accountant" ||
    user?.has_group_finance_access;
  if (isFinancePanel) {
    const gkItems: NavItem[] = [
      item("/group-finance", <BarChart3 size={16} />, "Panel finansowy GK"),
      item(
        "/finances/order-costs",
        <BarChart3 size={16} />,
        "Macierz kosztów zleceń",
      ),
    ];
    if (user?.is_ceo || user?.role === "hr" || user?.role === "accountant") {
      gkItems.push(
        item(
          "/finances/payroll-actuals",
          <Wallet size={16} />,
          "Urealnienie wypłat",
        ),
      );
    }
    if (user?.is_ceo) {
      gkItems.push(
        item(
          "/finances/project-groups",
          <Layers size={16} />,
          "Grupy przedsięwzięć",
        ),
      );
      gkItems.push(
        item(
          "/finances/group-forecast",
          <CalendarClock size={16} />,
          "Prognoza obłożenia",
        ),
      );
      gkItems.push(
        item(
          "/finances/salary-reconciliation",
          <Scale size={16} />,
          "Porównanie podziału płac",
        ),
      );
      gkItems.push(
        item(
          "/finances/non-project-smear",
          <Split size={16} />,
          "Podział kosztów obsługi",
        ),
      );
      gkItems.push(
        item(
          "/finances/contractor-layers",
          <Receipt size={16} />,
          "Koszty podwykonawcze",
        ),
      );
      gkItems.push(
        item(
          "/finances/order-simulations",
          <FlaskConical size={16} />,
          "Symulacja zlecenia",
        ),
      );
      gkItems.push(item("/ceo", <Shield size={16} />, "Zarządzanie dostępami"));
    }
    groups.push({ label: "Finanse GK", items: gkItems });
  }

  if (user?.role !== "contractor") {
    groups.push({
      label: "Zaopatrzenie",
      items: [
        item(
          "/supplies",
          <Package size={16} />,
          "Zapotrzebowania biurowe",
          true,
        ),
        item("/asset-registry", <Boxes size={16} />, "Ewidencja majątku", true),
        item("/fleet", <CarIcon size={16} />, "Flota samochodów", true),
        item("/couriers", <Truck size={16} />, "Zamawianie kuriera", true),
      ],
    });
  }

  // Podwykonawca bez zamówienia z contractor_fee_requests_allowed (flaga z umowy
  // ramowej) i bez historii próśb nie widzi sekcji wcale — backend i tak blokuje
  // mu tworzenie prośby. Brak pola (stara zalogowana sesja) = pokazuj.
  if (
    user?.role !== "contractor" ||
    user?.can_request_official_fees !== false
  ) {
    groups.push({
      label: "Opłaty urzędowe",
      items: [
        item("/official-fees", <Landmark size={16} />, "Opłaty urzędowe", true),
      ],
    });
  }

  // Faktury B2B — jeden wpis per rola:
  // - Pracownik na kontrakcie B2B — także były, dopóki ma faktury lub umowę B2B
  //   w historii: własna lista ("Moje faktury B2B")
  // - CEO i Księgowy: jeden ujednolicony widok administracyjny ("Faktury B2B")
  //   — zatwierdzanie i rozliczenie w tym samym miejscu, z filtrem statusu.
  const b2bItems: NavItem[] = [];
  if (showsMyB2BInvoices(user)) {
    b2bItems.push(
      item("/b2b-invoices", <Landmark size={16} />, "Moje faktury B2B", true),
    );
  }
  if (user?.is_ceo || user?.role === "accountant") {
    b2bItems.push(
      item(
        "/finances/b2b-invoices",
        <Landmark size={16} />,
        "Faktury B2B",
        true,
      ),
    );
  }
  if (b2bItems.length > 0) {
    groups.push({ label: "Faktury B2B", items: b2bItems });
  }

  // Employee-firma signing — visible only to signatories + CEO/admins
  const signingItems: NavItem[] = [];
  // "Moje rozliczenia podpisów" is hidden when the signatory has no periods,
  // or has periods with only free items — there is nothing to display.
  if (user?.is_signatory && hasPaidBillingPeriods) {
    signingItems.push(
      item(
        "/my-billing",
        <Landmark size={16} />,
        "Moje rozliczenia podpisów",
        true,
      ),
    );
  }
  if (user?.is_ceo) {
    signingItems.push(
      item(
        "/ceo/employee-agreements",
        <Shield size={16} />,
        "UR pracownik-firma",
        true,
      ),
      item(
        "/ceo/billing-overview",
        <Landmark size={16} />,
        "Rozliczenia podpisów pracowników",
        true,
      ),
    );
  }
  if (user?.role !== "contractor") {
    // All internal users can access the documentation panel.
    // Managers (director/coordinator/CEO) see the management label;
    // others see a generic label.
    const isDocManager =
      (user?.role === "director" && isTechnicalDirector) ||
      user?.role === "coordinator" ||
      user?.is_ceo;
    signingItems.push(
      item(
        "/documentation",
        <FileText size={16} />,
        isDocManager
          ? "Sprawdzenie dokumentacji projektowej"
          : "Dokumentacja projektowa",
      ),
    );
    if (user?.role === "director" && isTechnicalDirector) {
      signingItems.push(
        item(
          "/doc-activity",
          <ClipboardList size={16} />,
          "Dziennik aktywności projektantów sprawdzających",
        ),
      );
    }
  }
  if (signingItems.length > 0) {
    groups.push({ label: "Podpisywanie dokumentów", items: signingItems });
  }

  if (user?.role !== "contractor") {
    groups.push({
      label: "Urlopy",
      items: [
        item("/leaves", <CalendarDays size={16} />, "Moje urlopy", true),
        item("/sick-leaves", <Stethoscope size={16} />, "Moje L4", true),
        // Po przejściu na B2B zostaje, dopóki są karty / umowa UoP-UZ w historii.
        ...(showsMyTimesheets(user)
          ? [
              item(
                "/timesheets",
                <Clock3 size={16} />,
                "Moje karty godzin",
                true,
              ),
            ]
          : []),
      ],
    });

    const reviewLeaveItems: NavItem[] = [];
    if (
      user?.role === "director" ||
      user?.role === "hr" ||
      user?.role === "accountant" ||
      user?.role === "coordinator"
    ) {
      reviewLeaveItems.push(
        item("/leaves/review", <CalendarDays size={16} />, "Wnioski urlopowe"),
      );
    }
    if (
      user?.role === "director" ||
      user?.role === "hr" ||
      user?.role === "accountant"
    ) {
      reviewLeaveItems.push(
        item(
          "/schedule/review",
          <Monitor size={16} />,
          "Praca zdalna — wnioski",
        ),
        item(
          "/leaves/balances",
          <CalendarDays size={16} />,
          "Bilanse urlopowe",
        ),
      );
    }
    if (canReviewSickLeaves) {
      reviewLeaveItems.push(
        item(
          "/sick-leaves/review",
          <Stethoscope size={16} />,
          "L4 do zatwierdzenia",
        ),
      );
    }
    if (
      user?.role === "director" ||
      user?.role === "accountant" ||
      user?.role === "coordinator"
    ) {
      reviewLeaveItems.push(
        item("/timesheets/review", <Clock3 size={16} />, "Karty — przegląd"),
      );
    }
    if (
      user?.role === "hr" ||
      user?.role === "accountant" ||
      user?.is_ceo ||
      user?.has_multisport_access
    ) {
      reviewLeaveItems.push(
        item("/multisport", <Dumbbell size={16} />, "Multisport"),
      );
    }
    if (reviewLeaveItems.length > 0) {
      groups.push({ label: "Kadry", items: reviewLeaveItems });
    }
  }

  if (user) {
    const wsparceItems: NavItem[] = [
      item("/dev-issues", <Bug size={16} />, "Zgłoszenia techniczne", true),
    ];
    if (isDevOrDirector) {
      wsparceItems.push(
        item("/dev/metrics", <Activity size={16} />, "Metryki systemu", true),
      );
      wsparceItems.push(
        item(
          "/dev/activity",
          <Users size={16} />,
          "Aktywność użytkowników",
          true,
        ),
      );
      wsparceItems.push(
        item("/dev/accounts", <KeyRound size={16} />, "Konta", true),
      );
      wsparceItems.push(
        item(
          "/dev/bg-tasks",
          <ListTodo size={16} />,
          "Zadania automatyczne",
          true,
        ),
      );
      wsparceItems.push(
        item(
          "/dev/maintenance",
          <CalendarOff size={16} />,
          "Przerwy techniczne",
          true,
        ),
      );
      // Rejestr wysłanych SMS-ów (read-only) — ten sam krąg co reszta Wsparcia:
      // deweloper, CEO albo dyrektor techniczny.
      wsparceItems.push(
        item("/dev/sms", <MessageSquare size={16} />, "SMS", true),
      );
    }
    if (user?.is_developer) {
      wsparceItems.push(
        item(
          "/dev/credentials",
          <KeyRound size={16} />,
          "Rotacja kluczy",
          true,
        ),
      );
      wsparceItems.push(
        item(
          "/dev/file-scans",
          <ShieldCheck size={16} />,
          "Skany plików",
          true,
        ),
      );
    }
    // Wtyczki przeglądarkowe (dziś: automat ksiąg wieczystych) — instalacja, stanowiska,
    // zdalne ustawienia i dziennik. Developer zawsze; inni po jawnej fladze
    // nadawanej w Dostępach (has_browser_extensions_access).
    if (user?.is_developer || user?.has_browser_extensions_permission) {
      wsparceItems.push(
        item("/dev/extensions", <Puzzle size={16} />, "Wtyczki", true),
      );
    }
    groups.push({ label: "Wsparcie", items: wsparceItems });
  }

  if (
    user?.role === "director" ||
    user?.role === "accountant" ||
    user?.role === "hr"
  ) {
    const adminItems = [
      item("/employees", <Users size={16} />, "Pracownicy"),
      item("/accesses", <KeyRound size={16} />, "Dostępy"),
    ];
    // Przekazanie obowiązków — lustro ManagementCircleGuard i backendowego
    // IsTechnicalDirector: dyrektor techniczny albo CEO, nikt więcej.
    if (user?.is_ceo || (user?.role === "director" && !!user?.is_technical_director)) {
      adminItems.push(
        item(
          "/przekazanie-obowiazkow",
          <UserCog size={16} />,
          "Przekazanie obowiązków",
        ),
      );
    }
    if (user?.role === "director") {
      adminItems.push(
        item("/recruitment", <UserSearch size={16} />, "Rekrutacja"),
      );
      if (isTechnicalDirector) {
        adminItems.push(
          item(
            "/mgmt-backlog",
            <ClipboardList size={16} />,
            "Backlog zarządczy",
          ),
        );
        adminItems.push(item("/ceo", <Shield size={16} />, "Uprawnienia"));
      }
    }
    groups.push({ label: "Administracja", items: adminItems });
  }

  // ── DWA NIEZALEŻNE DLC — osobne sekcje, osobne badge'e/kolory ────────────────
  // Dostęp do każdego modułu DLC nadaje CEO per-pracownik (panel "Dostęp do modułów DLC").
  // CEO widzi wszystkie moduły; pozostali tylko nadane moduły. Podwykonawcy mogą dostać
  // wyłącznie DLC_01, DLC_02 i DLC_12 — backend odfiltrowuje resztę z accessible_dlc_modules.
  const canSeeDLC02 = dlcModules.includes("dlc_02");
  // Firmowy dziennik korespondencji (DLC_02) — uprawnienie "Firmowy obieg dokumentów"
  // albo widok dyrektorski (read-only, wszystkie wpływy). Fallback na uprawnienie
  // zarządzania obsługuje sesje zapisane sprzed dodania uprawnienia widoku.
  const canSeeCorrespondenceRegister =
    canSeeDLC02 &&
    !!(
      user?.has_correspondence_register_view_permission ??
      user?.has_correspondence_register_permission
    );
  const canSeeDLC03 = isInternal && dlcModules.includes("dlc_03");
  const canSeeDLC04 = isInternal && dlcModules.includes("dlc_04");
  const canSeeDLC06 = isInternal && dlcModules.includes("dlc_06");
  const canSeeDLC07 = isInternal && dlcModules.includes("dlc_07");
  const canSeeDLC09 = isInternal && dlcModules.includes("dlc_09");
  const canSeeDLC10 = isInternal && dlcModules.includes("dlc_10");

  // DLC_01 · ZE — projekty przyłączeniowe (przyłącza / działki / zgody). Moduł nadaje CEO
  // per-pracownik; sekcja stoi na dole, jako pierwsza z sekcji DLC.
  const canSeeDLC01 = dlcModules.includes("dlc_01");
  if (canSeeDLC01) {
    groups.push({
      label: "Projekty ZE",
      badge: {
        text: "DLC_01",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white`,
      },
      items: [
        // Plakietka „tytuły do potwierdzenia” — zgłoszenia firm prowadzących projekty
        // ZE czekające na nasze potwierdzenie (apps.contractor_led). Jedyny sygnał
        // z tego modułu — celowo bez powiadomień. Tylko dla tych, którzy mogą
        // potwierdzić (dyr. techniczny / CEO / flaga od CEO); backend liczy ją tylko im.
        item(
          "/power-projects",
          <Zap size={16} />,
          "Projekty",
          true,
          badges.led_titles_to_confirm > 0 && user?.has_led_title_confirmation_permission
            ? {
                text: `${badges.led_titles_to_confirm} tyt.`,
                cls: "bg-[var(--color-primary)] text-white",
                // Klik w plakietkę otwiera portfel zawężony do projektów, które czekają.
                to: "/power-projects?titles=pending",
                title: `${badges.led_titles_to_confirm} zgł. tytułów prawnych od firm prowadzących czeka na potwierdzenie — pokaż projekty`,
              }
            : undefined,
        ),
        // Kontrola procesu — Moje TODO, Zadania KPI i Arkusz AI jako zakładki jednej
        // strony. TODO/KPI: dyr. tech./CEO widzi wszystkich, zwykły prowadzący siebie
        // (scoping na backendzie); zakładka Arkusz AI tylko dla dyr. tech. + CEO.
        item(
          "/power-projects/kontrola",
          <ClipboardCheck size={16} />,
          "Kontrola procesu",
          false,
          undefined,
          // Analiza postępów (/kpi/analiza) to osobna strona, ale należy do tej pozycji.
          ["/power-projects/kpi/analiza"],
        ),
        // Skrzynka eop@ — korespondencja z ENERGA. Osobne uprawnienie WEWNĄTRZ
        // DLC_01: koordynatorzy przedsięwzięć ENERGA mają je z automatu, reszta po
        // fladze od CEO. Sam dostęp do modułu ZE nie wystarcza.
        // Licznik przy pozycji, nie tylko zakładka w środku modułu: zgłoszenie
        // wprost mówi, że szkice bez widocznego sygnału „będą cicho leżeć".
        ...(user?.has_eop_mail_permission
          ? [
              item(
                "/power-projects/poczta-eop",
                <Mail size={16} />,
                "Poczta EOP",
                false,
                eopWaiting > 0
                  ? {
                      text: String(eopWaiting),
                      cls: `${DLC_BADGE_CHIP} bg-[var(--color-primary)] text-white`,
                    }
                  : undefined,
              ),
            ]
          : []),
        // Działki — globalna lista działek ze wszystkich projektów ZE z bogatymi filtrami
        // (default: prowadzący = zalogowany użytkownik).
        item("/power-projects/dzialki", <LandPlot size={16} />, "Działki"),
        // Zamawianie materiałów — wypisy EGiB (per powiat), mapy zasadnicze (na obrys
        // MDCP), wypisy MPZP (per gmina) i zamówienia MDCP jako zakładki jednej strony.
        item(
          "/power-projects/zamawianie",
          <ShoppingCart size={16} />,
          "Zamawianie materiałów",
          false,
          undefined,
          // Szczegóły zamówień zostały pod starymi adresami — podświetlaj pozycję także tam.
          ["/power-projects/wypisy/", "/power-projects/mz/", "/power-projects/mpzp/"],
        ),
        // Ponaglenia — kalendarz telefonów kontrolnych: co poszło, co wróciło i o co
        // trzeba dziś dopytać (wnioski, pakiety WPW, wypisy, MPZP, MZ, MDCP).
        item(
          "/power-projects/ponaglenia",
          <PhoneCall size={16} />,
          "Ponaglenia",
          false,
          badges.control_calls_due > 0
            ? {
                text: String(badges.control_calls_due),
                cls: `${DLC_BADGE_CHIP} bg-amber-500 text-white`,
                title:
                  badges.control_calls_due === 1
                    ? "1 ponaglenie do wykonania — otwórz kalendarz"
                    : `${badges.control_calls_due} ponagleń do wykonania — otwórz kalendarz`,
              }
            : undefined,
        ),
        // Dokumenty z urzędów — skany odpowiedzi urzędów dopasowywane do zamówień
        // (wypisy EGiB, MZ, MPZP).
        item(
          "/power-projects/dokumenty-z-urzedow",
          <Landmark size={16} />,
          "Dokumenty z urzędów",
        ),
        // Wnioski urzędowe — silnik pakietów (lokalizacyjne, klauzule, WPW, odrolnienia, ULICP).
        {
          ...item("/power-projects/wnioski", <FileText size={16} />, "Wnioski i pakiety"),
          // Kolejka akceptacji ma własny wpis niżej — nie podświetlaj obu.
          notActiveUnder: ["/power-projects/wnioski/do-akceptacji"],
        },
        // Kolejka dyrektora technicznego: wnioski urzędowe ze wszystkich projektów
        // przekazane przez koordynatorów do akceptacji (zatwierdza dyr. techn. / CEO).
        // Obieg wyłączony 2026-09-30 (APPLICATION_APPROVAL_ENABLED) — pozycji nie ma.
        ...(APPLICATION_APPROVAL_ENABLED &&
        (user?.is_ceo || (user?.role === "director" && !!user?.is_technical_director))
          ? [
              item(
                "/power-projects/wnioski/do-akceptacji",
                <FileCheck size={16} />,
                "Wnioski do akceptacji",
                false,
                badges.applications_pending_approval > 0
                  ? {
                      text: String(badges.applications_pending_approval),
                      cls: `${DLC_BADGE_CHIP} bg-purple-600 text-white`,
                      title:
                        badges.applications_pending_approval === 1
                          ? "1 wniosek czeka na akceptację"
                          : `Wnioski czekające na akceptację: ${badges.applications_pending_approval}`,
                    }
                  : undefined,
              ),
            ]
          : []),
        // Rejestr pełnomocnictw + konfiguracje (urząd opłaty skarbowej, łańcuch per urząd).
        item(
          "/power-projects/pelnomocnictwa",
          <ShieldCheck size={16} />,
          "Pełnomocnictwa",
        ),
        // Rejestr „trudnych klientów" (karty specyfiki wszystkich poziomów) — widok
        // przeglądowy dla kierownictwa, nie codzienne narzędzie prowadzącego.
        item(
          "/power-projects/opieka",
          <HeartHandshake size={16} />,
          "Szczególna opieka",
        ),
        // Słowniki i presety modułu ZE (dawniej tylko w poziomym pasku widoków).
        // Presety = warstwy DXF (każdy) + metadane (zakładka tylko dla dyrektorów/CEO).
        item(
          "/power-projects/presety",
          <SlidersHorizontal size={16} />,
          "Presety",
        ),
        item(
          "/power-projects/designers",
          <Users size={16} />,
          "Projektanci i opracowujący",
        ),
        item(
          "/power-projects/contacts",
          <BookUser size={16} />,
          "Książka telefoniczna",
        ),
        // ZE Megamapa — portfolio-wide planning map, CEO / technical director only.
        ...(user?.is_ceo ||
        (user?.role === "director" && !!user?.is_technical_director)
          ? [
              item(
                "/power-projects/megamapa",
                <MapPinned size={16} />,
                "Megamapa",
              ),
              item(
                "/power-projects/doradca",
                <BrainCircuit size={16} />,
                "Doradca AI — nadzór",
              ),
            ]
          : []),
        item("/ze-tenders", <PencilRuler size={16} />, "Przetargi ZE", true),
        ...(isCeo || isTechnicalDirector || user?.has_ze_tender_analytics_access
          ? [
              item(
                "/ze-tenders/analiza",
                <BarChart3 size={16} />,
                "Analityka - przetargi ZE",
              ),
            ]
          : []),
        item(
          "/ze-tenders/eml-import",
          <Upload size={16} />,
          "Import przetargów ENERGA",
        ),
        item("/doc-generation", <FileText size={16} />, "Generacja dokumentów"),
        // Rejestr urzędów jest wspólny dla modułów, a mieszka w sekcji obiegu dokumentów.
        // Kto prowadzi działki, ale nie ma DLC_02, nie miał do niego wejścia — i zamiast
        // poprawić literówkę w urzędzie zakładał duplikat. Pokazujemy go tutaj dokładnie
        // wtedy, gdy sekcja DLC_02 się nie pojawi (inaczej pozycja byłaby podwójna).
        ...(!canSeeDLC02
          ? [item("/paper-send/offices", <Landmark size={16} />, "Urzędy")]
          : []),
        ...(isCeo
          ? [
              item(
                "/ze-tenders/capabilities",
                <Shield size={16} />,
                "Uprawnienia ENERGA",
              ),
            ]
          : []),
      ],
    });
  } else if (dlcModules.includes("dlc_01_min")) {
    // Minimalny dostęp do DLC_01 — z całego modułu tylko zbiorczy widok działek.
    groups.push({
      label: "Projekty ZE",
      badge: {
        text: "DLC_01",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white`,
      },
      items: [item("/power-projects/dzialki", <LandPlot size={16} />, "Działki")],
    });
  }

  // DLC_02 · Obieg dokumentów — wysyłka papierowa (niezależny od ZE, generyczny)
  if (canSeeDLC02) {
    groups.push({
      label: "Obieg dokumentów",
      badge: {
        text: "DLC_02",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-emerald-500 to-teal-500 text-white`,
      },
      items: [
        // Firmowy rejestr — tylko z uprawnieniem; "Moja korespondencja" widoczna dla wszystkich.
        ...(canSeeCorrespondenceRegister
          ? [
              item(
                "/paper-send",
                <Send size={16} />,
                "Dziennik korespondencji",
                true,
              ),
            ]
          : []),
        item(
          "/paper-send/mine",
          <Inbox size={16} />,
          "Moja korespondencja",
          true,
        ),
        item(
          "/paper-send/print-orders",
          <Printer size={16} />,
          "Zlecenia wydruku",
          true,
        ),
        item("/paper-send/offices", <Landmark size={16} />, "Urzędy"),
      ],
    });
  }

  // DLC_03 · ARON — asystent AI nad systemem (czyta przez API w granicach uprawnień usera)
  if (canSeeDLC03) {
    groups.push({
      label: "ARON",
      badge: {
        text: "DLC_03",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-red-600 via-red-700 to-black text-white`,
      },
      items: [
        item("/aron", <Bot size={16} />, "Asystent ARON", true),
        item("/aron/tasks", <CalendarClock size={16} />, "Zadania cykliczne"),
        item("/aron/mail", <Mail size={16} />, "Poczta ARON"),
        // CEO-only ARON admin (both pages are CEO-guarded) — grouped under DLC_03.
        ...(user?.is_ceo
          ? [
              item(
                "/ceo/aron-knowledge",
                <BookOpen size={16} />,
                "Baza wiedzy ARON",
              ),
              item("/ceo/aron-meta", <BrainCircuit size={16} />, "META-ARON"),
              item("/ceo/aron-expenses", <Wallet size={16} />, "Wydatki ARON"),
              item(
                "/ceo/aron-mail-aliases",
                <AtSign size={16} />,
                "Aliasy poczty ARON",
              ),
            ]
          : []),
        // Cross-user audit (full transcripts) — super-admin: CEO + DEV only.
        ...(user?.is_ceo && user?.is_developer
          ? [item("/ceo/aron-audit", <Eye size={16} />, "Audyt ARON")]
          : []),
      ],
    });
  }

  // DLC_04 · ENG-ARON — AI-recenzent opracowań projektowych z własną bazą wiedzy
  // (wytyczne + katalog błędów historycznych); niezależny od firmowej wiedzy ARONa.
  if (canSeeDLC04) {
    groups.push({
      label: "ENG-ARON",
      badge: {
        text: "DLC_04",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-amber-500 to-orange-600 text-white`,
      },
      items: [
        item("/eng-aron", <FileSearch size={16} />, "Recenzje opracowań", true),
        item("/eng-aron/knowledge", <BookOpen size={16} />, "Baza wiedzy"),
        item("/eng-aron/findings", <History size={16} />, "Historia uwag"),
        item("/eng-aron/chat", <Bot size={16} />, "Czat ENG-ARON"),
      ],
    });
  }

  // DLC_06 · FIN-ARON — finanse projektów ZE: koordynatorzy × pensje × marże + doradca
  // finansowy AI. Dane pensyjne — dostęp wyłącznie z grantem dlc_06 (CEO zawsze).
  if (canSeeDLC06) {
    groups.push({
      label: "FIN-ARON",
      badge: {
        text: "DLC_06",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-sky-600 to-cyan-500 text-white`,
      },
      items: [
        item(
          "/fin-aron",
          <BarChart3 size={16} />,
          "Koordynatorzy × marże",
          true,
        ),
        item(
          "/fin-aron/advisor",
          <BrainCircuit size={16} />,
          "Rekomendacje AI",
        ),
        item("/fin-aron/knowledge", <BookOpen size={16} />, "Baza wiedzy FIN"),
        item("/fin-aron/overhead", <Wallet size={16} />, "Narzuty"),
      ],
    });
  }

  // DLC_07 · EZ-ARON — analiza przetargów BZP (ezamowienia.gov.pl): feed okazji,
  // bramka wydatkowa AI (podgląd kandydatów), baza wiedzy. Grant dlc_07 (CEO zawsze).
  if (canSeeDLC07) {
    groups.push({
      label: "EZ-ARON",
      badge: {
        text: "DLC_07",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-pink-500 to-rose-600 text-white`,
      },
      items: [
        item(
          "/ezamowienia",
          <Landmark size={16} />,
          "Oferty przetargowe",
          true,
        ),
        item("/ezamowienia/chat", <Bot size={16} />, "Czat EZ-ARON"),
        item(
          "/ezamowienia/benchmarks",
          <BarChart3 size={16} />,
          "Benchmarki cen",
        ),
        item(
          "/ezamowienia/candidates",
          <BrainCircuit size={16} />,
          "Kandydaci AI",
        ),
        item(
          "/ezamowienia/knowledge",
          <BookOpen size={16} />,
          "Baza wiedzy EZ",
        ),
      ],
    });
  }

  // DLC_09 · e-Doręczenia — kolejka wysyłki dla wszystkich z modułem; skrzynka odbiorcza
  // i konfiguracja skrzynek tylko dla CEO (pisma z sądów/US/ZUS).
  if (canSeeDLC09) {
    groups.push({
      label: "e-Doręczenia",
      badge: {
        text: "DLC_09",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-indigo-500 to-violet-500 text-white`,
      },
      items: [
        item("/edoreczenia", <Send size={16} />, "Kolejka wysyłki", true),
        ...(user?.is_ceo
          ? [
              item(
                "/edoreczenia/skrzynka",
                <Inbox size={16} />,
                "Skrzynka odbiorcza",
              ),
              item(
                "/edoreczenia/skrzynki",
                <Landmark size={16} />,
                "Skrzynki i certyfikaty",
              ),
            ]
          : []),
      ],
    });
  }

  // DLC_10 · Obliczenia techniczne — obliczenia sił na słupach linii nN.
  if (canSeeDLC10) {
    groups.push({
      label: "Obliczenia techniczne",
      badge: {
        text: "DLC_10",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-lime-500 to-emerald-600 text-white`,
      },
      items: [
        item("/tech-calc", <Calculator size={16} />, "Projekty obliczeń", true),
        // Katalog norm — tylko z dodatkowym uprawnieniem (CEO/developer/flaga).
        ...(user?.has_tech_calc_norms_permission
          ? [item("/tech-calc/norms", <BookOpen size={16} />, "Katalog norm")]
          : []),
      ],
    });
  }

  // DLC_12 · Narzędzia PDF — moduł nadawany w panelu CEO; dyrektorzy techniczni
  // i CEO mają go z urzędu (backend dokłada go do accessible_dlc_modules).
  if (dlcModules.includes("dlc_12")) {
    groups.push({
      label: "Narzędzia PDF",
      badge: {
        text: "DLC_12",
        cls: `${DLC_BADGE_GLOSS} bg-gradient-to-r from-amber-500 to-rose-600 text-white`,
      },
      items: [item("/pdf", <FileStack size={16} />, "Narzędzia PDF", true)],
    });
  }

  // Dynamic notification dots for a given nav route. Centralised here so the
  // same source drives both the per-item badges (expanded group) and the
  // aggregate badge on a collapsed group header. Order within a case is the
  // render order; a zero/missing count is dropped by `add`.
  const dotsFor = (to: string): DotSpec[] => {
    const d: DotSpec[] = [];
    const add = (
      count: number | null | undefined,
      color: DotColor,
      title?: string,
    ) => {
      if ((count ?? 0) > 0) d.push({ count: count as number, color, title });
    };
    switch (to) {
      case "/finances/payroll-actuals":
        if (canSeePayrollBadge) add(payrollMissingCount, "red");
        break;
      case "/finances/invoices-prz":
        if (user?.role === "accountant") {
          add(przDraftCount, "amber");
          add(przProformaToConvertCount, "amber");
        }
        if (user?.role === "director") add(przNeedsApprovalCount, "amber");
        if (isCeo) add(przInvestorCount, "amber");
        break;
      case "/aron":
        add(
          aronPendingWrites?.chat,
          "red",
          "Propozycje zapisu czekają na Twoje zatwierdzenie",
        );
        break;
      case "/aron/tasks":
        add(
          aronScheduledUnread,
          "red",
          "Nowe wyniki zadań cyklicznych — kliknij plakietkę na karcie, żeby odznaczyć",
        );
        break;
      case "/aron/mail":
        add(
          aronPendingWrites?.mail,
          "red",
          "Propozycje zapisu z Twoich maili czekają na zatwierdzenie",
        );
        break;
      case "/ceo/aron-knowledge":
        add(pendingKnowledgeCount, "red");
        break;
      case "/eng-aron":
        add(engReviewsToReviewCount, "amber");
        break;
      case "/accounting/invoices":
        if (user?.role === "director") add(pendingInvoicesCount, "amber");
        if (user?.role === "accountant") add(invoicesNeedsActionCount, "amber");
        break;
      case "/protocols":
        if (user?.role === "accountant" || user?.role === "hr" || isCeo)
          add(protocolsNeedingHrMandateCount, "red");
        break;
      case "/contractor/orders":
        add(pendingOrdersCount, "brand");
        break;
      case "/contractor/protocols":
        add(pendingProtocolsCount, "amber");
        add(protocolsNeedingInvoiceCount, "red");
        break;
      case "/contractor/proceedings":
        add(
          openProceedingsCount,
          "brand",
          "Otwarte postępowania, w których nie złożyliście jeszcze oferty",
        );
        break;
      case "/requisitions":
        if (user?.role === "director") add(pendingRequisitionsCount, "amber");
        add(resultFilesUploadedCount, "brand", "Podwykonawca przesłał pliki");
        break;
      case "/orders":
        if (isGK) {
          add(pendingChangeReqCount, "amber");
          add(pendingCommentsCount, "red");
          add(pendingExternalCostsCount, "amber");
          add(
            staleUnconfirmedOrders?.count,
            "red",
            "Zamówienia długo czekające na przyjęcie przez podwykonawcę (po terminie przyjęcia)",
          );
        }
        add(
          ordersAwaitingMyRating,
          "black",
          "Zakończone zamówienia czekające na Twoją ocenę podwykonawcy",
        );
        break;
      case "/weekly-reports":
        if (isGK) add(weeklyReportUnreadCount, "brand");
        break;
      case "/contractor/weekly-reports":
        add(weeklyReportPendingCount, "amber");
        break;
      case "/contractor/documentation":
        add(docPendingCount, "amber");
        break;
      case "/documentation":
        if (isGK) {
          add(docAwaitingCount, "amber");
          add(docUnacknowledgedCount, "blue");
        }
        if (user?.is_signatory) add(docPendingCount, "amber");
        break;
      case "/agreements":
      case "/contractor/agreements":
        add(insuranceAlertCount, "red");
        break;
      case "/tasks/board": {
        // Jeden badge: moje aktywne podzadania + liczniki sekcji „Postęp prac"
        // (widoki wyprowadzone z menu do Friday). Kolor bierze najpilniejszy z
        // nich — po wejściu w Friday sekcja rozdziela je z powrotem.
        const fridayDots: DotSpec[] = [];
        if (myActiveCount > 0)
          fridayDots.push({
            count: myActiveCount,
            color: "brand",
            title: "Moje aktywne podzadania",
          });
        if (workProgressCount > 0)
          fridayDots.push({
            count: workProgressCount,
            color: workProgressTone,
            title: "Postęp prac — wymaga działania",
          });
        const merged = aggregateDots(fridayDots);
        if (merged) d.push(merged);
        break;
      }
      case "/paper-send/mine":
        add(registerCounts?.to_acknowledge ?? 0, "red");
        // Moje pisma czekające w kolejce wysyłki (zakładka „Moje wysyłane") — wpis wychodzący
        // nie jest na nikogo dekretowany, więc bez tego badge autor po wysłaniu listu nie miał
        // w menu żadnego znaku, że coś jego stoi w kolejce działu obiegu.
        add(registerCounts?.my_dispatch ?? 0, "amber");
        break;
      case "/paper-send":
        if (user?.has_correspondence_register_permission) {
          add(
            (registerCounts?.to_file ?? 0) + (registerCounts?.to_send ?? 0),
            "amber",
          );
          add(registerCounts?.returned ?? 0, "red");
        }
        break;
      case "/power-projects":
        add(ppAttention?.projects ?? 0, "amber");
        break;
      case "/tenders":
        if (user?.has_approve_pricing_permission && showBadge.needs_approval)
          add(pendingApprovals, "amber", TENDER_BADGE_TITLES.needs_approval);
        if (user?.has_issue_offers_permission && showBadge.needs_offer)
          add(
            tenderActionCounts?.needs_offer ?? 0,
            "red",
            TENDER_BADGE_TITLES.needs_offer,
          );
        if (user?.has_calculate_pricing_permission && showBadge.needs_pricing)
          add(
            tenderActionCounts?.needs_pricing ?? 0,
            "brand",
            TENDER_BADGE_TITLES.needs_pricing,
          );
        if (user?.has_issue_offers_permission && showBadge.needs_result)
          add(
            tenderActionCounts?.needs_result ?? 0,
            "rose",
            TENDER_BADGE_TITLES.needs_result,
          );
        break;
      case "/ze-tenders":
        if (user?.has_approve_pricing_permission && showBadge.needs_approval)
          add(
            zeActionCounts?.needs_approval ?? 0,
            "amber",
            TENDER_BADGE_TITLES.needs_approval,
          );
        if (user?.has_issue_offers_permission && showBadge.needs_offer)
          add(
            zeActionCounts?.needs_offer ?? 0,
            "red",
            TENDER_BADGE_TITLES.needs_offer,
          );
        if (user?.has_calculate_pricing_permission && showBadge.needs_pricing)
          add(
            zeActionCounts?.needs_pricing ?? 0,
            "brand",
            TENDER_BADGE_TITLES.needs_pricing,
          );
        if (user?.has_issue_offers_permission && showBadge.needs_result)
          add(
            zeActionCounts?.needs_result ?? 0,
            "rose",
            TENDER_BADGE_TITLES.needs_result,
          );
        break;
      case "/employees":
        if (isDirector) add(expiringContractsCount, "amber");
        if (canSeeHealthBadge) add(healthTodoCount, "red");
        break;
      case "/recruitment":
        if (isDirectorOrHR) add(todayInterviewsCount, "brand");
        break;
      case "/timesheets/review":
        if (user?.role === "director") add(pendingTimesheetsCount, "amber");
        break;
      case "/timesheets":
        if (user?.role !== "contractor" && timesheetMissing)
          add(1, TIMESHEET_MISSING_BADGE_COLOR, TIMESHEET_MISSING_BADGE_TITLE);
        break;
      case "/supplies":
        // Same people as the backend gate: whoever can place supply orders
        // (director/coordinator) plus the is_supply_manager flag.
        if (
          user?.is_supply_manager ||
          user?.role === "director" ||
          user?.role === "coordinator"
        )
          add(suppliesPendingCount, "amber");
        break;
      case "/asset-registry":
        if (user?.role === "director" || user?.is_ceo)
          add(assetsPendingCount, "amber");
        break;
      case "/fleet":
        if (user?.is_fleet_manager) add(fleetTodo?.total ?? 0, "red");
        break;
      case "/couriers":
        if (user?.has_small_account_access) add(couriersPendingCount, "amber");
        break;
      case "/paper-send/print-orders":
        // Badge for every DLC_02 user — realization is open to the whole DLC_02 audience; the
        // backend count is already tailored per user (approvers also get the awaiting-approval part).
        if (canSeeDLC02) add(printOrdersPendingCount, "amber");
        break;
      case "/rfq/scope-requests":
        // Rozstrzyga dyrektor techniczny (CEO też) — backend liczy tylko dla nich.
        add(pendingScopeRequestsCount, "amber");
        break;
      case "/rfq/proceedings":
        // Postępowania po zamkniętej turze, w których nie zapadł jeszcze wynik.
        // Backend liczy je w zakresie widoczności pytającego (kupiec — swoje).
        add(proceedingsToResolveCount, "red");
        break;
      case "/rfq/account-requests":
        // Prośby o konto z publicznej strony — konta zakłada dyr. tech / CEO.
        add(accountRequestsCount, "amber");
        break;
      case "/leaves/review":
        if (isDirectorOrHR) add(pendingLeavesCount, "red");
        break;
      case "/schedule/review":
        if (isDirectorOrHR) add(pendingRemoteWorkCount, "amber");
        break;
      case "/sick-leaves/review":
        if (canReviewSickLeaves) add(pendingSickLeavesCount, "amber");
        break;
      case "/whats-new":
        add(whatsNewUnreadCount, "amber");
        break;
      case "/dev-issues":
        if (user?.is_developer || user?.is_ceo) add(openIssuesCount, "red");
        break;
      case "/dev/metrics":
        if (user?.is_developer) add(lastTestRunBugCount, "red");
        break;
      case "/dev/credentials":
        if (user?.is_developer) add(credentialAlertCount, "amber");
        break;
      case "/official-fees":
        add(
          pendingFeeApprovalCount,
          OFFICIAL_FEE_BADGE_COLORS.pending_approval,
          OFFICIAL_FEE_BADGE_TITLES.pending_approval,
        );
        add(
          pendingFeePaymentCount,
          OFFICIAL_FEE_BADGE_COLORS.pending_payment,
          OFFICIAL_FEE_BADGE_TITLES.pending_payment,
        );
        add(
          pendingFeeAcknowledgementCount,
          OFFICIAL_FEE_BADGE_COLORS.pending_acknowledgement,
          OFFICIAL_FEE_BADGE_TITLES.pending_acknowledgement,
        );
        break;
      case "/b2b-invoices":
        for (const key of B2B_EMPLOYEE_BADGES)
          add(b2bCounts?.[key], B2B_BADGE_COLORS[key], B2B_BADGE_TITLES[key]);
        break;
      case "/finances/b2b-invoices":
        // Backend zeruje koszyk roli, której użytkownik nie pełni.
        add(
          b2bCounts?.to_approve,
          B2B_BADGE_COLORS.to_approve,
          B2B_BADGE_TITLES.to_approve,
        );
        add(
          b2bCounts?.to_settle,
          B2B_BADGE_COLORS.to_settle,
          B2B_BADGE_TITLES.to_settle,
        );
        break;
      case "/expenses":
        if (isAccountantOrDirector) {
          add(expenseCounts?.pendingAcceptance ?? 0, "amber");
          if (user?.role === "accountant") {
            add(expenseCounts?.pendingPayment ?? 0, "brand");
            add(expenseCounts?.pendingBooking ?? 0, "blue");
          }
        }
        break;
    }
    return d;
  };

  // Active-route detection for group headers (mirrors NavLink's `end` logic) so
  // a collapsed group still signals which section the open view lives in.
  const { pathname } = useLocation();
  const linkIsActive = (link: NavItem) => {
    const end = link.to === "/" || !!link.end;
    if (link.to === "/") return pathname === "/";
    if (underAny(pathname, link.notActiveUnder)) return false;
    return end
      ? pathname === link.to
      : pathname === link.to || pathname.startsWith(`${link.to}/`);
  };

  // The user's own section order (set in reorder mode) wins over the code order.
  const orderedGroups = applyNavGroupOrder(
    timesheetLocked ? lockedToTimesheetGroups(groups) : groups,
    navGroupOrder,
  );

  const searchQuery = sidebarSearchEnabled
    ? normalizeForSearch(navQuery.trim())
    : "";
  const visibleGroups = searchQuery
    ? orderedGroups
        .map((group) =>
          normalizeForSearch(group.label).includes(searchQuery)
            ? group
            : {
                ...group,
                items: group.items.filter((link) =>
                  normalizeForSearch(link.label).includes(searchQuery),
                ),
              },
        )
        .filter((group) => group.items.length > 0)
    : orderedGroups;

  // Labels of every collapsible group — target of the "expand/collapse all" buttons.
  const collapsibleLabels = orderedGroups.map((g) => g.label).filter(Boolean);
  // Sections the user may reorder: every labeled group, in its current order.
  const reorderSections = orderedGroups
    .filter((g) => !!g.label)
    .map((g) => ({ label: g.label, badge: g.badge }));

  return (
    <nav
      className={clsx(
        "flex flex-col py-0",
        "bg-[var(--color-bg-surface)] border-r border-[var(--color-border)]",
        "fixed inset-y-0 left-0 z-40 w-64",
        "transform transition-transform duration-200 ease-in-out",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "lg:static lg:inset-auto lg:z-auto lg:w-64 lg:translate-x-0 lg:flex-shrink-0",
      )}
    >
      {/* Logo */}
      <div className="px-4 py-4 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-2"
            aria-label="Strona główna"
          >
            {isInternal && (
              <img
                ref={logoRef}
                src="/logo500_pidgeotto.png"
                alt="ELEKTIM logo"
                className="h-7 w-7 object-contain flex-shrink-0 cursor-pointer"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                onMouseEnter={showLogoTooltip}
                onMouseLeave={hideLogoTooltip}
              />
            )}
            {logoTooltipPos &&
              createPortal(
                <div
                  className="pointer-events-none fixed z-[9999] flex flex-col items-start"
                  style={{ top: logoTooltipPos.top, left: logoTooltipPos.left }}
                >
                  <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white dark:border-b-gray-800 ml-3.5" />
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-2">
                    <img
                      src="/logo500_pidgeotto.png"
                      alt="ELEKTIM logo"
                      className="w-20 h-20 object-contain"
                    />
                  </div>
                </div>,
                document.body,
              )}
            <span className="font-mono text-lg font-bold text-[var(--color-primary)] uppercase tracking-tight">
              ELGW
            </span>
            <span className="font-mono text-lg font-bold text-[var(--color-text-primary)] uppercase tracking-tight">
              5.3
            </span>
          </Link>
          {isInternal && (
            <span className="font-mono text-[9px] font-bold border border-amber-400 text-amber-600 px-1 py-0.5 uppercase tracking-widest leading-none">
              {"PIDGEOTTO"}
            </span>
          )}
          <button
            onClick={onClose}
            className="ml-auto lg:hidden p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
            aria-label="Zamknij menu"
          >
            <X size={16} />
          </button>
        </div>
        <p className="section-label mt-0.5">GK ELEKTIM</p>
      </div>

      {/* Navigation */}
      <div className="sidebar-scroll flex flex-col flex-1 overflow-y-auto py-2">
        {reorderMode && (
          <SidebarSectionReorder
            sections={reorderSections}
            isCustomised={navGroupOrder.length > 0}
            onChange={setNavGroupOrder}
            onReset={resetNavGroupOrder}
            onDone={() => setReorderMode(false)}
          />
        )}
        {!reorderMode && searchQuery && visibleGroups.length === 0 && (
          <p className="px-4 py-2 text-xs font-mono text-[var(--color-text-muted)]">
            Brak wyników
          </p>
        )}
        {!reorderMode &&
          visibleGroups.map((group, gi) => {
            const hasHeader = !!(group.label || group.badge);
            // Only labeled groups get a collapse toggle (the Dashboard row has no
            // header). Search always renders matches expanded.
            const collapsible = hasHeader && !!group.label;
            const collapsed =
              collapsible && !searchQuery && !!collapsedNavGroups[group.label];
            const containsActive = group.items.some(linkIsActive);
            // Collapsed → roll every item's dots into one badge on the header.
            const agg = collapsed
              ? aggregateDots(group.items.flatMap((l) => dotsFor(l.to)))
              : null;
            // Expand-all / collapse-all buttons live on the right of the headerless
            // Dashboard row (first group); hidden while the quick-filter is active.
            const showBulkButtons = gi === 0 && !hasHeader && !searchQuery;
            return (
              <div key={gi}>
                {gi > 0 && (
                  <div className="h-px bg-[var(--color-border-muted)] mx-3 my-1" />
                )}
                {hasHeader &&
                  (collapsible ? (
                    <button
                      type="button"
                      onClick={() => toggleNavGroup(group.label)}
                      aria-expanded={!collapsed}
                      className="w-full flex items-center gap-1.5 px-4 pt-2 pb-1 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
                    >
                      <span
                        className="flex-shrink-0"
                        style={{
                          color: containsActive
                            ? "var(--color-primary)"
                            : "var(--color-text-muted)",
                        }}
                      >
                        {collapsed ? (
                          <ChevronRight size={12} />
                        ) : (
                          <ChevronDown size={12} />
                        )}
                      </span>
                      {group.badge && <DlcBadge badge={group.badge} />}
                      {group.label && (
                        <span
                          className="sidebar-section-label min-w-0 flex-1 break-words"
                          style={
                            containsActive
                              ? { color: "var(--color-primary)" }
                              : undefined
                          }
                        >
                          {group.label}
                        </span>
                      )}
                      {agg && (
                        <span className="ml-auto flex items-center">
                          <Dot
                            count={agg.count}
                            color={agg.color}
                            title={agg.title}
                          />
                        </span>
                      )}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 px-4 pt-2 pb-1">
                      {group.badge && <DlcBadge badge={group.badge} />}
                      {group.label && (
                        <p className="sidebar-section-label">{group.label}</p>
                      )}
                    </div>
                  ))}
                {!collapsed && (
                  <div
                    className={
                      showBulkButtons ? "flex items-center" : undefined
                    }
                  >
                    <div
                      className={showBulkButtons ? "flex-1 min-w-0" : undefined}
                    >
                      {group.items.map((link) => (
                        <NavLink
                          key={link.to}
                          to={link.to}
                          end={link.to === "/" || !!link.end}
                          onClick={() => {
                            setNavQuery("");
                            onClose();
                          }}
                          className={({ isActive }) =>
                            clsx(
                              "flex items-center gap-2.5 px-4 py-1.5 mx-2 text-xs font-mono font-medium transition-colors duration-100",
                              "border-l-2",
                              (isActive && !underAny(pathname, link.notActiveUnder)) ||
                                (link.alsoActive ?? []).some((p) => pathname.startsWith(p))
                                ? "bg-[var(--color-bg-elevated)] text-[var(--color-primary)] border-[var(--color-primary)]"
                                : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] border-transparent",
                            )
                          }
                        >
                          <span className="flex-shrink-0 opacity-70">
                            {link.icon}
                          </span>
                          <span className="flex-1">{link.label}</span>

                          {link.badge && <DlcBadge badge={link.badge} />}

                          {dotsFor(link.to).map((dot, di) => (
                            <Dot
                              key={di}
                              count={dot.count}
                              color={dot.color}
                              title={dot.title}
                            />
                          ))}
                        </NavLink>
                      ))}
                    </div>
                    {showBulkButtons && (
                      <div className="flex items-center gap-0.5 pr-3 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            setNavGroupsCollapsed(collapsibleLabels, false)
                          }
                          title="Rozwiń wszystko"
                          aria-label="Rozwiń wszystkie sekcje"
                          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                        >
                          <ChevronsUpDown size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setNavGroupsCollapsed(collapsibleLabels, true)
                          }
                          title="Zwiń wszystko"
                          aria-label="Zwiń wszystkie sekcje"
                          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                        >
                          <ChevronsDownUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNavQuery("");
                            setReorderMode(true);
                          }}
                          title="Zmień kolejność sekcji"
                          aria-label="Zmień kolejność sekcji menu"
                          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                        >
                          <ArrowUpDown size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Nav quick-filter — pinned above the footer; can be hidden in /settings.
          Hidden in reorder mode, where the links themselves are not rendered. */}
      {sidebarSearchEnabled && !reorderMode && (
        <div className="border-t border-[var(--color-border)] px-2 py-2">
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)] focus-within:border-[var(--color-primary)] transition-colors">
            <Search
              size={13}
              className="text-[var(--color-text-muted)] flex-shrink-0"
            />
            <input
              type="text"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setNavQuery("");
              }}
              placeholder="Filtruj menu…"
              aria-label="Filtruj pozycje menu"
              className="w-full min-w-0 bg-transparent text-xs font-mono text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none"
            />
            {navQuery && (
              <button
                type="button"
                onClick={() => setNavQuery("")}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] flex-shrink-0 transition-colors"
                aria-label="Wyczyść filtr"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* User avatar + Settings — pinned bottom */}
      <div className="border-t border-[var(--color-border)] px-2 py-2">
        {user && (
          <NavLink
            to="/settings"
            onClick={onClose}
            className={({ isActive }) =>
              clsx(
                "flex items-center gap-2.5 px-3 py-2 rounded-md transition-colors duration-100",
                isActive
                  ? "bg-[var(--color-bg-elevated)]"
                  : "hover:bg-[var(--color-bg-elevated)]",
              )
            }
          >
            {user.profile_picture_url ? (
              <img
                src={user.profile_picture_url}
                alt="Avatar"
                className="w-7 h-7 rounded-full object-cover border border-[var(--color-border)] flex-shrink-0"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-[var(--color-primary)] flex items-center justify-center text-[var(--color-text-inverse)] text-[10px] font-mono font-bold select-none flex-shrink-0">
                {`${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() ||
                  "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono font-medium text-[var(--color-text-primary)] truncate uppercase tracking-wide leading-tight">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-[10px] font-mono text-[var(--color-text-muted)] truncate uppercase tracking-widest leading-tight">
                Ustawienia
              </p>
            </div>
            {navBadgesLoading && (
              <Loader2
                size={13}
                className="opacity-50 flex-shrink-0 animate-spin text-[var(--color-text-muted)]"
                aria-label="Pobieranie powiadomień"
              />
            )}
            <Settings size={13} className="opacity-40 flex-shrink-0" />
          </NavLink>
        )}
      </div>
      {isInternal && <PushNotificationBanner />}
    </nav>
  );
}
